import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { useCan } from '../../lib/session';
import type { ImportDomain, ImportJobListItem } from './types';
import { SkeletonRows } from '../skeleton';

const STATUS_TONE: Record<string, 'default' | 'info' | 'success' | 'danger' | 'warn'> = {
  UPLOADED: 'default',
  MAPPED: 'default',
  VALIDATING: 'info',
  VALIDATED: 'warn',
  VALIDATION_FAILED: 'danger',
  EXECUTING: 'info',
  COMPLETED: 'success',
  EXECUTION_FAILED: 'danger',
  CANCELLED: 'default',
};

function StatusBadge({ status }: { readonly status: string }): React.JSX.Element {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status] ?? 'default';
  return (
    <span className={tone === 'default' ? 'badge' : `badge badge--${tone}`}>
      {t(`importExport.status${status}`)}
    </span>
  );
}

function ErrorLine({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="field-error">
      {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
    </p>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** 导入中心（S17，§33）。列表 + 新建（选域 + 上传），映射/校验/预览/确认/执行都在详情页完成。 */
export function ImportJobsListPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canImport = useCan(PERMISSIONS.IMPORT_EXECUTE);
  const [showCreate, setShowCreate] = useState(false);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data, isPending } = useQuery({
    queryKey: ['import-jobs', 'list'],
    queryFn: () => apiFetch<{ items: ImportJobListItem[] }>('/import-jobs'),
  });

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.importCenter')}</h1>
        {canImport && (
          <button type="button" className="btn btn--primary" onClick={() => setShowCreate(true)}>
            {t('importExport.newImport')}
          </button>
        )}
      </div>

      {isPending ? (
        <SkeletonRows />
      ) : (data?.items.length ?? 0) === 0 ? (
        <div className="empty-state">
          <p>{t('importExport.emptyImports')}</p>
        </div>
      ) : (
        <div className="table" style={{ display: 'block' }}>
          <div
            className="row"
            style={{ fontWeight: 600, padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}
          >
            <span style={{ width: '160px' }}>{t('importExport.colDoc')}</span>
            <span style={{ width: '160px' }}>{t('importExport.colDomain')}</span>
            <span style={{ width: '160px' }}>{t('importExport.colStatus')}</span>
            <span style={{ width: '160px' }}>{t('importExport.colRows')}</span>
            <span style={{ width: '160px' }}>{t('importExport.colCreatedAt')}</span>
          </div>
          {data!.items.map((job) => (
            <Link
              key={job.id}
              to={`/import-jobs/${job.id}`}
              className="tree__row"
              style={{ height: '48px', textDecoration: 'none', color: 'inherit' }}
            >
              <span style={{ width: '160px' }}>{job.docNo}</span>
              <span style={{ width: '160px' }}>{t(`importExport.domain${job.domain}`)}</span>
              <span style={{ width: '160px' }}>
                <StatusBadge status={job.status} />
              </span>
              <span style={{ width: '160px' }}>
                {t('importExport.rowsSummary', {
                  total: job.rowCount,
                  valid: job.validCount,
                  error: job.errorCount,
                })}
              </span>
              <span style={{ width: '160px' }}>{fmt.format(new Date(job.createdAt))}</span>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateImportModal
          onClose={() => setShowCreate(false)}
          onCreated={(id, headers, suggestedMapping) => {
            setShowCreate(false);
            void queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
            navigate(`/import-jobs/${id}`, { state: { headers, suggestedMapping } });
          }}
        />
      )}
    </div>
  );
}

const DOMAIN_OPTIONS: readonly ImportDomain[] = ['PRODUCTS', 'SUPPLIERS', 'LOCATIONS', 'INITIAL_INVENTORY'];

function CreateImportModal({
  onClose,
  onCreated,
}: {
  readonly onClose: () => void;
  readonly onCreated: (id: string, headers: string[], suggestedMapping: Record<string, string>) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [domain, setDomain] = useState<ImportDomain>('PRODUCTS');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('no file');
      const dataBase64 = await fileToBase64(file);
      return apiFetch<{
        id: string;
        rowCount: number;
        headers: string[];
        suggestedMapping: Record<string, string>;
      }>('/import-jobs', {
        method: 'POST',
        body: { domain, filename: file.name, contentType: file.type, dataBase64 },
      });
    },
    onSuccess: (res) => onCreated(res.id, res.headers, res.suggestedMapping),
  });

  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <h2>{t('importExport.newImport')}</h2>
        <label className="field">
          <span>{t('importExport.selectDomain')}</span>
          <select value={domain} onChange={(e) => setDomain(e.target.value as ImportDomain)}>
            {DOMAIN_OPTIONS.map((d) => (
              <option key={d} value={d}>
                {t(`importExport.domain${d}`)}
              </option>
            ))}
          </select>
        </label>

        <div
          className="row"
          style={{
            marginTop: 'var(--space-3)',
            padding: 'var(--space-5)',
            border: `2px dashed ${dragging ? 'var(--color-accent)' : 'var(--color-border)'}`,
            borderRadius: 'var(--radius-md)',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--space-2)',
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const dropped = e.dataTransfer.files[0];
            if (dropped) setFile(dropped);
          }}
        >
          {file ? (
            <span>{file.name}</span>
          ) : (
            <span className="field-hint">{t('importExport.dropHint')}</span>
          )}
          <input
            type="file"
            accept=".xlsx,.csv"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <ErrorLine error={createMutation.error} />
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('app.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={!file || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? t('app.loading') : t('importExport.uploadSubmit')}
          </button>
        </div>
      </div>
    </div>
  );
}
